import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

export interface PhoneInputUKProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

// Client-side hint only — pkg/phone on the server is authoritative.
export function looksLikeUKPhone(raw: string): boolean {
  const v = raw.replace(/[\s().-]/g, '');
  if (v.startsWith('+')) return /^\+\d{8,15}$/.test(v);
  return /^0\d{9,10}$/.test(v);
}

export const PhoneInputUK = forwardRef<HTMLInputElement, PhoneInputUKProps>(
  ({ id, label, hint, error, className, disabled, ...rest }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

    return (
      <div className="flex w-full flex-col gap-[6px]">
        {label ? (
          <label htmlFor={inputId} className="font-body text-body-sm font-medium text-text-primary">
            {label}
          </label>
        ) : null}
        <div
          className={clsx(
            'flex h-14 w-full overflow-hidden rounded-s bg-surface-primary font-body text-body-md text-text-primary',
            disabled && 'bg-surface-tertiary text-text-secondary',
            error
              ? 'border-[1.5px] border-border-error focus-within:border-border-error focus-within:ring-2 focus-within:ring-border-error focus-within:ring-offset-1'
              : 'border border-border-input focus-within:border-[1.5px] focus-within:border-text-primary',
          )}
        >
          <span
            aria-hidden="true"
            className={clsx(
              'flex shrink-0 items-center border-r border-border-input bg-surface-secondary px-4 text-text-secondary',
              disabled && 'bg-surface-tertiary',
            )}
          >
            +44
          </span>
          <input
            ref={ref}
            id={inputId}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="07400 123456"
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            disabled={disabled}
            className={clsx(
              'h-full min-w-0 flex-1 border-none bg-transparent px-4 font-body text-body-md text-text-primary outline-none',
              'placeholder:font-body placeholder:text-body-md placeholder:text-text-secondary',
              'disabled:cursor-not-allowed disabled:text-text-secondary',
              className,
            )}
            {...rest}
          />
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="font-body text-body-xs font-medium text-text-error">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="font-body text-body-xs font-medium text-text-secondary">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);
PhoneInputUK.displayName = 'PhoneInputUK';
